import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowLeft, Package, MapPin, Calendar, ExternalLink,
  Workflow, Shield, Leaf, AlertTriangle, CheckCircle,
  Inbox, ClipboardCheck, Plus, X, Archive, ArchiveRestore,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ChainOfCustodyTimeline, { CANONICAL_STAGES } from '../components/ChainOfCustodyTimeline';
import ChainStatusBadge from '../components/ChainStatusBadge';
import ProductTraceabilityCheckpoints from '../components/ProductTraceabilityCheckpoints';
import BatchRecordingPanel from '../components/BatchRecordingPanel';
import AuditTrailList from '../components/AuditTrailList';
import NfcManagementCard from '../components/NfcManagementCard';
import TemperatureMonitoringCard from '../components/TemperatureMonitoringCard';
import permissions from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BatchDetail = ({ user }) => {
  const { id } = useParams();
  const perms = permissions(user?.role);
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [audit, setAudit] = useState([]);
  const [correctionForm, setCorrectionForm] = useState(null); // {gap, prev_stage, next_stage}
  const [archiveBusy, setArchiveBusy] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('mvp_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggleArchive = async () => {
    if (!batch || archiveBusy) return;
    const isArchived = !!batch.archived;
    const verb = isArchived ? 'unarchive' : 'archive';
    if (!isArchived) {
      const ok = window.confirm(
        `Archive "${batch.product_name || batch.id}"?\n\nThe batch will be hidden from active dashboards, monitoring views, and batch lists. All audit, chain-of-custody, compliance, NFC, and temperature history will be preserved and accessible through the Archived filter.`,
      );
      if (!ok) return;
    }
    setArchiveBusy(true);
    try {
      const token = localStorage.getItem('mvp_token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const url = `${BACKEND_URL}/api/batches/${encodeURIComponent(batch.id)}/${verb}`;
      const body = isArchived ? {} : { reason: 'Demo session complete' };
      const { data } = await axios.post(url, body, config);
      setBatch(data);
      toast.success(isArchived ? 'Batch restored to active list' : 'Batch archived — history preserved');
    } catch (err) {
      const msg = err?.response?.data?.detail || `Could not ${verb} batch`;
      toast.error(msg);
    } finally {
      setArchiveBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const cfg = { headers: authHeader() };
        const [batchRes, eventsRes, auditRes, correctionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/batches/${id}`).catch((e) => ({ error: e })),
          axios.get(`${BACKEND_URL}/api/batches/${id}/events`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/batches/${id}/audit-trail`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/batches/${id}/manual-corrections`, cfg).catch(() => ({ data: [] })),
        ]);
        if (batchRes.error) {
          setError('Batch not found');
        } else {
          setBatch(batchRes.data);
        }
        setEvents(eventsRes.data || []);
        setAudit(auditRes.data || []);
        setCorrections(correctionsRes.data || []);
      } catch (e) {
        setError('Failed to load batch details');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /* --------------- Derived state: stages + gaps --------------- */
  const stageMap = useMemo(() => {
    const m = {};
    for (const ev of events) {
      const key = (ev.stage_key || ev.event_type || '').toLowerCase().replace(/\s+/g, '_');
      if (CANONICAL_STAGES.find((s) => s.key === key)) m[key] = ev;
    }
    return m;
  }, [events]);

  const resolvedGapKeys = useMemo(
    () => new Set(corrections.filter((c) => c.status === 'resolved').map((c) => c.missing_stage_key)),
    [corrections],
  );

  const gaps = useMemo(() => {
    const out = [];
    if (events.length === 0) return out;
    // A gap = a stage that has both a previous AND a later stage recorded but itself is missing
    const recordedKeys = Object.keys(stageMap);
    const recordedIdxs = recordedKeys
      .map((k) => CANONICAL_STAGES.findIndex((s) => s.key === k))
      .filter((i) => i >= 0);
    if (recordedIdxs.length < 2) return out;
    const minIdx = Math.min(...recordedIdxs);
    const maxIdx = Math.max(...recordedIdxs);
    for (let i = minIdx + 1; i < maxIdx; i++) {
      const stage = CANONICAL_STAGES[i];
      if (!stageMap[stage.key] && !resolvedGapKeys.has(stage.key)) {
        out.push({
          missing: stage,
          prev: CANONICAL_STAGES[i - 1],
          next: CANONICAL_STAGES[i + 1],
        });
      }
    }
    return out;
  }, [events, stageMap, resolvedGapKeys]);

  /* --------------- Manual correction submit --------------- */
  const submitCorrection = async (payload) => {
    try {
      const cfg = { headers: authHeader() };
      const body = {
        missing_stage_key: payload.missing_stage_key,
        missing_stage_label: payload.missing_stage_label,
        previous_stage: payload.previous_stage,
        next_stage: payload.next_stage,
        reason: payload.reason,
        corrective_action: payload.corrective_action,
        evidence_label: payload.evidence_label || null,
      };
      const { data: correction } = await axios.post(
        `${BACKEND_URL}/api/batches/${id}/manual-corrections`,
        body,
        cfg,
      );
      setCorrections((prev) => [correction, ...prev]);
      // Refresh audit trail so the mirrored row shows up immediately.
      try {
        const a = await axios.get(`${BACKEND_URL}/api/batches/${id}/audit-trail`);
        setAudit(a.data || []);
      } catch { /* audit refresh failure is non-fatal */ }
      setCorrectionForm(null);
      toast.success('Manual correction logged. Audit trail updated.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to log manual correction';
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Empty / not-found state — structure preserved with empty-state messaging
  if (error || !batch) {
    return (
      <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="batch-detail-empty">
        <Button variant="ghost" onClick={() => navigate('/mvp/batches')} className="-ml-2 h-10">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Batches
        </Button>
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-6 sm:p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
            <Inbox className="w-7 h-7 text-slate-400" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Batch <span className="font-mono">{id}</span> not yet registered
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
            This batch is not yet registered in PureSustain.
          </p>
        </div>

        {/* Chain of Custody — structure preserved, empty state */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8" data-testid="chain-of-custody-section">
          <ChainOfCustodyTimeline />
        </section>
      </div>
    );
  }

  const qrUrl = `${window.location.origin}/verify/${id}`;

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="batch-detail-page">
      {/* Back nav + archive action */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => navigate('/mvp/batches')} className="-ml-2 h-10">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Batches
        </Button>
        {perms.canRegisterProduct && (
          <Button
            variant="outline"
            onClick={toggleArchive}
            disabled={archiveBusy}
            data-testid={batch.archived ? 'unarchive-batch-button' : 'archive-batch-button'}
            className="h-10"
          >
            {batch.archived ? (
              <>
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Restore from archive
              </>
            ) : (
              <>
                <Archive className="w-4 h-4 mr-2" />
                Archive batch
              </>
            )}
          </Button>
        )}
      </div>

      {/* Archived banner */}
      {batch.archived && (
        <div
          data-testid="archived-batch-banner"
          className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4"
        >
          <Archive className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-semibold text-amber-900">This batch is archived</p>
            <p className="text-amber-800 leading-snug">
              Hidden from active dashboards, monitoring, and batch lists. Audit, chain-of-custody, compliance, NFC and temperature history remain fully preserved below.
              {batch.archive_reason && (
                <span className="block mt-1 text-xs">Reason: <em>{batch.archive_reason}</em></span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Header + QR card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Batch identity */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="flex items-start gap-3 flex-wrap mb-3">
            <Package className="w-6 h-6 text-slate-700" />
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-mono text-slate-500 truncate">{batch.batch_number || batch.id}</p>
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 break-words"
                style={{ fontFamily: 'Manrope' }}
              >
                {batch.product_name || 'Unnamed product'}
              </h1>
              <p className="text-sm sm:text-base text-slate-600 italic">{batch.species || '—'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
            <IdentityField label="Origin" value={batch.origin?.name} sub={batch.origin?.region} icon={MapPin} />
            <IdentityField label="Supplier" value={batch.supplier || '—'} icon={Package} />
            <IdentityField label="Catch / Harvest" value={formatDate(batch.catch_date)} sub={formatTime(batch.catch_date)} icon={Calendar} />
            <IdentityField label="Current Location" value={batch.current_location || '—'} icon={MapPin} />
            <IdentityField label="Current Status" value={batch.current_status || '—'} icon={Workflow} />
            <IdentityField label="Weight" value={batch.weight_kg ? `${batch.weight_kg.toLocaleString()} kg` : '—'} icon={Package} />
          </div>

          {/* Status pills */}
          <div className="flex flex-wrap gap-2 mt-4 sm:mt-6 pt-4 border-t border-slate-100">
            <StatusPill icon={Workflow} label="Traceability" ready={events.length > 0 && gaps.length === 0} />
            <StatusPill icon={Leaf} label="Sustainability" ready={false} emptyText="Pending records" />
            <StatusPill icon={Shield} label="Audit Readiness" ready={gaps.length === 0 && events.length > 0} emptyText={gaps.length > 0 ? `${gaps.length} gap(s)` : 'Pending records'} />
            <StatusPill icon={ClipboardCheck} label="Compliance" ready={Array.isArray(batch.compliance) && batch.compliance.length > 0} emptyText="Pending events" />
          </div>
        </div>

        {/* QR verification card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 flex flex-col items-center text-center">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Public Verification
          </p>
          <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: 'Manrope' }}>
            QR Verification
          </h3>
          <div className="bg-white p-3 rounded-lg border border-slate-200">
            <QRCodeSVG value={qrUrl} size={140} level="M" />
          </div>
          <p className="text-[11px] text-slate-500 break-all mt-3 max-w-full">{qrUrl}</p>
          <a
            href={qrUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-customer-qr-view"
            className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg w-full h-10"
          >
            <ExternalLink className="w-4 h-4" />
            Open Customer QR View
          </a>
        </div>
      </div>

      {/* NFC sticker management — bind/clear physical NFC tags */}
      <NfcManagementCard
        batch={batch}
        canManage={perms.canManageNfcTag}
        onUpdated={(updated) => setBatch(updated || batch)}
      />

      {/* Temperature & Cold-Chain — start/end session + record readings
          scoped to THIS batch. Backend contract is unchanged. */}
      <TemperatureMonitoringCard
        batch={batch}
        canManage={perms.canIngestSensorReading}
      />

      {/* Product Traceability Checkpoints — operator action surface */}
      {perms.canRunAnyCheckpoint && (
        <ProductTraceabilityCheckpoints
          batchId={id}
          role={user?.role}
          onRecorded={async () => {
            const [eventsRes, auditRes, batchRes] = await Promise.all([
              axios.get(`${BACKEND_URL}/api/batches/${id}/events`).catch(() => ({ data: [] })),
              axios.get(`${BACKEND_URL}/api/batches/${id}/audit-trail`).catch(() => ({ data: [] })),
              axios.get(`${BACKEND_URL}/api/batches/${id}`).catch(() => ({ data: batch })),
            ]);
            setEvents(eventsRes.data || []);
            setAudit(auditRes.data || []);
            setBatch(batchRes.data || batch);
          }}
        />
      )}

      {/* Sustainability + Compliance recording surfaces */}
      {(perms.canLogSustainabilityMetric || perms.canLogComplianceStandard) && (
        <BatchRecordingPanel
          batchId={id}
          onLogged={async () => {
            const [auditRes, batchRes] = await Promise.all([
              axios.get(`${BACKEND_URL}/api/batches/${id}/audit-trail`).catch(() => ({ data: [] })),
              axios.get(`${BACKEND_URL}/api/batches/${id}`).catch(() => ({ data: batch })),
            ]);
            setAudit(auditRes.data || []);
            setBatch(batchRes.data || batch);
          }}
        />
      )}

      {/* Chain of Custody — primary, always-visible section */}
      <section className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8" data-testid="chain-of-custody-section">
        <ChainOfCustodyTimeline
          events={events}
          corrections={corrections}
        />
      </section>

      {/* Traceability Gap Detection */}
      {gaps.length > 0 && (
        <section data-testid="traceability-gaps-section" className="space-y-3">
          {gaps.map((g) => (
            <div
              key={g.missing.key}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5"
              data-testid={`gap-${g.missing.key}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-semibold text-amber-900">
                      Traceability gap detected: Missing {g.missing.label} event between {g.prev.label} and {g.next.label}.
                    </p>
                    <p className="text-xs sm:text-sm text-amber-800 mt-1 leading-relaxed">
                      <strong>Impact:</strong> Audit-readiness reduced — the chain-of-custody record cannot be reconstructed end-to-end without this event.
                      <br />
                      <strong>Suggested action:</strong> Log a manual correction with operator name, timestamp, reason, and (optional) evidence reference.
                    </p>
                  </div>
                </div>
                {perms.canRunManualCorrection && (
                  <Button
                    onClick={() => setCorrectionForm(g)}
                    data-testid={`log-correction-${g.missing.key}`}
                    className="bg-amber-700 hover:bg-amber-800 text-white h-10 self-start sm:self-auto flex-shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Log Manual Correction
                  </Button>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Manual Correction Log */}
      <section data-testid="manual-correction-log-section">
        <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
          Manual Correction Log
        </h2>
        {corrections.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 sm:p-8 text-center">
            <ClipboardCheck className="w-7 h-7 text-slate-400 mx-auto mb-3" />
            <p className="text-sm sm:text-base text-slate-600">No corrections recorded yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {corrections.map((c) => (
              <div key={c.id} data-testid={`correction-${c.id}`} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-semibold text-slate-900">
                      {c.missing_stage_label} <span className="text-slate-500 font-normal">— between {c.previous_stage} and {c.next_stage}</span>
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">{c.reason}</p>
                    <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
                      {c.user} · {new Date(c.timestamp).toLocaleString()} · ID: <span className="font-mono">{c.id}</span>
                    </p>
                    {c.corrective_action && (
                      <p className="text-xs sm:text-sm text-slate-700 mt-2">
                        <strong>Corrective action:</strong> {c.corrective_action}
                      </p>
                    )}
                    {c.evidence_label && (
                      <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
                        Evidence: <span className="italic">{c.evidence_label}</span>
                      </p>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                    <CheckCircle className="w-3 h-3" /> Resolved
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Audit Trail (auto-generated by checkpoint events) */}
      <section data-testid="audit-trail-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Audit Trail
          </h2>
          <ChainStatusBadge batchId={id} />
        </div>
        <AuditTrailList entries={audit} />
      </section>

      {/* Correction modal */}
      {correctionForm && (
        <ManualCorrectionForm
          gap={correctionForm}
          user={user}
          onClose={() => setCorrectionForm(null)}
          onSubmit={submitCorrection}
        />
      )}
    </div>
  );
};

/* ----------------------- subcomponents ----------------------- */

const IdentityField = ({ label, value, sub, icon: Icon }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5 text-slate-500 mb-1">
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
      <span className="text-[10px] sm:text-xs uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-sm sm:text-base font-medium text-slate-900 break-words">{value || '—'}</p>
    {sub && <p className="text-[11px] sm:text-xs text-slate-500 truncate">{sub}</p>}
  </div>
);

const StatusPill = ({ icon: Icon, label, ready, emptyText }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-medium border ${
      ready ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}: {ready ? 'Ready' : (emptyText || 'Pending')}
  </span>
);

const ManualCorrectionForm = ({ gap, user, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [action, setAction] = useState('');
  const [evidence, setEvidence] = useState('');
  const canSubmit = reason.trim() && action.trim();
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="correction-form-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
              Log Manual Correction
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Missing <strong>{gap.missing.label}</strong> between <strong>{gap.prev.label}</strong> and <strong>{gap.next.label}</strong>
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded h-10 w-10 flex items-center justify-center" data-testid="correction-form-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <FieldDisplay label="Missing event" value={gap.missing.label} />
          <FieldDisplay label="Previous stage" value={gap.prev.label} />
          <FieldDisplay label="Next stage" value={gap.next.label} />
          <FieldDisplay label="Operator" value={user?.name || '—'} />
          <FieldDisplay label="Timestamp" value={new Date().toLocaleString()} />
          <FieldInput label="Reason for correction *" value={reason} onChange={setReason} testId="correction-reason" placeholder="e.g. Event was not auto-logged due to barcode scanner outage." multiline />
          <FieldInput label="Corrective action *" value={action} onChange={setAction} testId="correction-action" placeholder="e.g. Cross-checked physical log; entered missing Quality Inspection from QC supervisor's paper records." multiline />
          <FieldInput label="Evidence reference (optional)" value={evidence} onChange={setEvidence} testId="correction-evidence" placeholder="e.g. QC log #2026-W22-014, photo upload pending" />
        </div>
        <div className="p-4 sm:p-6 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button
            data-testid="correction-submit"
            disabled={!canSubmit}
            onClick={() => onSubmit({
              missing_stage_key: gap.missing.key,
              missing_stage_label: gap.missing.label,
              previous_stage: gap.prev.label,
              next_stage: gap.next.label,
              reason: reason.trim(),
              corrective_action: action.trim(),
              evidence_label: evidence.trim() || null,
            })}
            className="bg-amber-700 hover:bg-amber-800 text-white h-10"
          >
            Submit Correction
          </Button>
        </div>
      </div>
    </div>
  );
};

const FieldDisplay = ({ label, value }) => (
  <div>
    <label className="block text-[11px] sm:text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</label>
    <p className="text-sm sm:text-base text-slate-900 font-medium">{value}</p>
  </div>
);

const FieldInput = ({ label, value, onChange, placeholder, testId, multiline }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {multiline ? (
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
      />
    ) : (
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 px-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
      />
    )}
  </div>
);

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatTime = (d) => (d ? new Date(d).toLocaleTimeString() : '');

export default BatchDetail;
